import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState, useEffect, useCallback } from "react";
import { Alert, Box, Typography } from "@mui/material";
import Loader from "../../Loader";
import Input from "../Input";
import Button from "../Button";
import { CreateLeiService } from "../../../../api/services/create-lei";
import { useNavigate } from "react-router-dom";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useDispatch } from "react-redux";
import { fetchDataForm } from "../../../../redux/action-creators/dataFormReducer";

const countries = [
    "United Kingdom",
    "Denmark",
    "Estonia",
    "Finland",
    "Lithuania",
    "Latvia",
];

interface IPaymentMessage {
    severity: "success" | "error";
    text: React.ReactNode;
}

interface ICardFormProps {
    price: number | string;
}

const CardForm: React.FC<ICardFormProps> = ({ price }) => {
    const [cardError, setCardError] = useState("");
    const [email, setEmail] = useState("");
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState<boolean>(false);
    const [paymentMessage, setPaymentMessage] = useState<IPaymentMessage | null>(null);
    const navigate = useNavigate();
    const [trackingId, setTrackingId] = useState("");
    const [country, setCountry] = useState(countries[0]);
    const { dataDataForm } = useTypedSelector((state) => state.dataForm);
    const dispatch = useDispatch();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Handle real-time validation errors from the card Element.
    const handleChange = (event: any) => {
        setCardError(event.error ? event.error.message : "");
    }

    const handleError = (error: any) => {
        console.log(error.response.data)
        if ("non_field_errors" in error.response.data) {
            return "You have already applied for this LEI."
        }
        return error["message"];
    };

    const SaveOrderStatus = useCallback(async () => {
        if (trackingId !== "") {
            await dispatch(fetchDataForm({ "trackingId": trackingId }))
            navigate("/lerd");
        }
    }, [trackingId]);

    useEffect(() => {
        SaveOrderStatus();
    }, [trackingId]);

    // Handle form submission.
    const handleSubmit = async (event: any) => {
        setLoading(true);
        event.preventDefault();
        const card = elements!.getElement(CardElement);
        const { paymentMethod, error } = await stripe!.createPaymentMethod({
            type: "card",
            card: card!,
        });

        if (error) {
            setCardError(error.type);
            setPaymentMessage({ severity: "error", text: "Payment system error" });
            setLoading(false);
        } else {
            try {
                const response = await CreateLeiService.createLeiCode({
                    ...dataDataForm,
                    price,
                    email,
                    country,
                    payment_method_id: paymentMethod.id,
                });
                console.log("response", response.data);
                setPaymentMessage({ severity: "success", text: "Payment success" });
                setLoading(false);
                setTrackingId(response.data.order_tracking_code);
            } catch (error: any) {
                setPaymentMessage({ severity: "error", text: `Error: ${handleError(error)}` });
                setLoading(false);
                setTrackingId("");
            }
        };
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500 }}>
            <Input
                placeholder="Email Address"
                type="email"
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                isRequired
                uiProps={{ margin: "10px 0 20px" }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Credit or debit card
            </Typography>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: "14px",
                    mb: cardError ? 1 : "20px",
                }}
            >
                <CardElement id="card-element" onChange={handleChange} />
            </Box>
            {cardError && (
                <Alert severity="error" sx={{ mb: "20px" }}>
                    {cardError}
                </Alert>
            )}

            <Input
                type="select"
                placeholder="Country"
                value={country}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountry(event.target.value)}
                options={countries}
                uiProps={{ margin: "10px 0 20px" }}
            />

            <Button type="submit" styleType="primary">Submit Payment</Button>

            {paymentMessage && (
                <Alert severity={paymentMessage.severity} sx={{ mt: 2 }}>
                    {paymentMessage.text}
                </Alert>
            )}
            {loading ? <Loader /> : ""}
        </Box>
    );
};

export default CardForm;
